import productBackImageManifest from '@/generated/productBackImageManifest.json';

function hasRasterExtension(value) {
  return typeof value === 'string' && /\.(avif|webp|jpe?g|png)$/i.test(value);
}

function isBaseFallback(value) {
  return typeof value === 'string' && /\/base(?:_|)[12]_/i.test(value);
}

export function pickRasterImage(maybeArrayOrString, fallback = '') {
  if (!maybeArrayOrString) return fallback || '';
  if (Array.isArray(maybeArrayOrString)) {
    const raster = maybeArrayOrString.find(hasRasterExtension);
    return raster || maybeArrayOrString[0] || fallback || '';
  }
  return maybeArrayOrString;
}

export function rewriteLegacyProductImage(src, sku) {
  if (!src || typeof src !== 'string') return src;
  try {
    const match = src.match(/^(.*)\/base_(1|2)(\.[^.]+)?$/);
    if (match) {
      const ext = match[3] || '.png';
      return `/product_images/${sku}/base${match[2]}_${sku}${ext}`;
    }
  } catch {}
  return src;
}

export function deriveImageBase(src) {
  if (!src || typeof src !== 'string') return null;
  const match = src.match(/(.+?)\.[^.]+$/);
  return match ? match[1] : null;
}

function pickPlainColorKey(product) {
  if (!product?.sku) return '';
  const candidateColorKeys = ['white', ...(Array.isArray(product.colors) ? product.colors : [])];

  for (const colorKey of [...new Set(candidateColorKeys)]) {
    const colorSrc = pickRasterImage(product.images?.[colorKey], '');
    const rewritten = rewriteLegacyProductImage(colorSrc, product.sku);
    if (rewritten && !isBaseFallback(rewritten)) {
      return colorKey;
    }
  }

  return '';
}

function pickPlainFrontImage(product) {
  const colorKey = pickPlainColorKey(product);
  if (!colorKey) return '';
  const colorSrc = pickRasterImage(product?.images?.[colorKey], '');
  return rewriteLegacyProductImage(colorSrc, product.sku);
}

function pickPlainBackColorKey(product, generatedBackImages) {
  const preferredColorKey = pickPlainColorKey(product);
  if (preferredColorKey && pickRasterImage(generatedBackImages?.[preferredColorKey], '')) {
    return preferredColorKey;
  }

  if (product?.images?.white && pickRasterImage(generatedBackImages?.white, '')) {
    return 'white';
  }

  return '';
}

export function getProductSideImage(product, side = 'front', selectedColors = [], options = {}) {
  if (!product || !product.sku) return '';

  const normalizedSide = side === 'back' ? 'back' : 'front';
  const primaryColor = Array.isArray(selectedColors) && selectedColors.length ? selectedColors[0] : null;
  const fallbackKey = normalizedSide === 'back' ? 'base2' : 'base1';
  const fallbackPath = `/product_images/${product.sku}/${fallbackKey}_${product.sku}.png`;
  const generatedBackImages = productBackImageManifest?.[product.sku] || null;
  const usePlainFrontWhenNoColor = Boolean(options.usePlainFrontWhenNoColor);

  if (normalizedSide === 'front' && primaryColor && product.images?.[primaryColor]) {
    const colorSrc = pickRasterImage(product.images[primaryColor], fallbackPath);
    return rewriteLegacyProductImage(colorSrc, product.sku);
  }

  if (normalizedSide === 'front' && !primaryColor && usePlainFrontWhenNoColor) {
    return pickPlainFrontImage(product) || fallbackPath;
  }

  if (normalizedSide === 'back' && generatedBackImages) {
    const candidateColors = [];

    if (primaryColor) {
      candidateColors.push(primaryColor);
    } else if (usePlainFrontWhenNoColor) {
      const plainBackColorKey = pickPlainBackColorKey(product, generatedBackImages);
      if (plainBackColorKey) {
        candidateColors.push(plainBackColorKey);
      }
    } else if (product.images?.white) {
      candidateColors.push('white');
    }

    for (const colorKey of [...new Set(candidateColors)]) {
      const generatedBack = pickRasterImage(generatedBackImages[colorKey], '');
      if (generatedBack) return generatedBack;
    }
  }

  const baseSrc = pickRasterImage(product.images?.[fallbackKey], fallbackPath);
  return rewriteLegacyProductImage(baseSrc, product.sku);
}
